import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats', icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads', icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm', icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents', icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets', icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot', icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api', icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding', icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget', icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics', icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing', icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings', icon: '⚙️', label: 'Settings' }
];

const QUESTION_TYPES = [
  { value: 'text',   label: '📝 Text',     desc: 'Free text answer' },
  { value: 'email',  label: '📧 Email',    desc: 'Email address' },
  { value: 'phone',  label: '📞 Phone',    desc: 'Phone number' },
  { value: 'number', label: '🔢 Number',   desc: 'Numeric answer' },
  { value: 'select', label: '📋 Dropdown', desc: 'Choose from options' },
  { value: 'yesno',  label: '✅ Yes / No', desc: 'Simple yes or no' }
];

const PLAN_LIMITS = { pack_one: 4, pack_two: 12, pack_three: 20, pack_advanced: 999 };

function QuestionForm({ data, setData, optionInput, setOptionInput, addOption, removeOption }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Question Text *</label>
        <input
          value={data.question_text}
          onChange={e => setData(p => ({ ...p, question_text: e.target.value }))}
          placeholder="e.g. What is your company name?"
          autoFocus
        />
      </div>
      <div>
        <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Answer Type</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {QUESTION_TYPES.map(t => (
            <div key={t.value} onClick={() => setData(p => ({ ...p, question_type: t.value }))}
              style={{ padding: '10px', border: `1px solid ${data.question_type === t.value ? '#00d55b' : '#1e1e1e'}`, background: data.question_type === t.value ? 'rgba(0,213,91,0.08)' : '#111', borderRadius: 8, cursor: 'pointer', textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: data.question_type === t.value ? 600 : 400, color: data.question_type === t.value ? '#00d55b' : '#888' }}>{t.label}</div>
              <div style={{ fontSize: 10, color: '#444', marginTop: 2 }}>{t.desc}</div>
            </div>
          ))}
        </div>
      </div>
      {data.question_type === 'select' && (
        <div>
          <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Dropdown Options</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input value={optionInput} onChange={e => setOptionInput(e.target.value)} placeholder="Type an option" onKeyDown={e => e.key === 'Enter' && addOption()} style={{ flex: 1 }} />
            <button onClick={() => addOption()} style={{ padding: '0 14px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', border: 'none' }}>Add</button>
          </div>
          {(data.options || []).map((opt, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#0d0d0d', borderRadius: 6, marginBottom: 4 }}>
              <span style={{ flex: 1, fontSize: 13, color: '#fff' }}>{opt}</span>
              <button onClick={() => removeOption(i)} style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer', fontSize: 14 }}>✕</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#0d0d0d', borderRadius: 8 }}>
        <div>
          <div style={{ fontSize: 13, color: '#fff' }}>Required</div>
          <div style={{ fontSize: 11, color: '#555' }}>Visitor must answer this question</div>
        </div>
        <div onClick={() => setData(p => ({ ...p, is_required: !p.is_required }))}
          style={{ width: 44, height: 24, borderRadius: 12, background: data.is_required ? '#00d55b' : '#333', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
          <div style={{ position: 'absolute', top: 3, left: data.is_required ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('business');
  const [questions, setQuestions] = useState([]);
  const [cannedReplies, setCannedReplies] = useState([]);
  const [showAddQ, setShowAddQ] = useState(false);
  const [editQ, setEditQ] = useState(null);
  const [optionInput, setOptionInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [newQ, setNewQ] = useState({ question_text: '', question_type: 'text', options: [], is_required: true });
  const [newCanned, setNewCanned] = useState({ title: '', message: '', shortcut: '' });

  // Business details form
  const [bizForm, setBizForm] = useState({ company_name: '', owner_name: '', phone: '', address: '', state: '', gstin: '' });
  const [bizLoading, setBizLoading] = useState(false);

  // Ads Integration
  const [adsForm, setAdsForm] = useState({ meta_page_id: '', google_ads_secret: '' });
  const [adsLoading, setAdsLoading] = useState(false);
  const [hasAdsIntegration, setHasAdsIntegration] = useState(user?.has_ads_integration || false);

  const planLimit = PLAN_LIMITS[user?.plan_key] || 4;
  const isUnlimited = user?.plan_key === 'pack_advanced';

  useEffect(() => {
    loadQuestions();
    loadCanned();
    // Load business details
    axios.get('/api/business/profile').then(r => {
      const d = r.data.data;
      if (d) {
        setBizForm({ company_name: d.company_name || '', owner_name: d.owner_name || '', phone: d.phone || '', address: d.address || '', state: d.state || '', gstin: d.gstin || '' });
        setHasAdsIntegration(!!d.has_ads_integration);
      }
    }).catch(() => {});
    // Load ads integration settings
    axios.get('/api/business/ads-integration').then(r => {
      if (r.data.data) setAdsForm({ meta_page_id: r.data.data.meta_page_id || '', google_ads_secret: r.data.data.google_ads_secret || '' });
    }).catch(() => {});
  }, []);

  const saveAdsSettings = async () => {
    setAdsLoading(true);
    try {
      await axios.put('/api/business/ads-integration', adsForm);
      toast.success('Ads integration settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setAdsLoading(false);
    }
  };

  const loadQuestions = () => axios.get('/api/business/prechat-questions').then(r => setQuestions(r.data.data || [])).catch(() => {});
  const loadCanned = () => axios.get('/api/business/canned-replies').then(r => setCannedReplies(r.data.data || [])).catch(() => {});

  const saveBizDetails = async () => {
    setBizLoading(true);
    try {
      await axios.put('/api/business/details', bizForm);
      toast.success('Business details updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally { setBizLoading(false); }
  };

  const addQuestion = async () => {
    if (!newQ.question_text.trim()) return toast.error('Please enter a question');
    if (newQ.question_type === 'select' && newQ.options.length < 2) return toast.error('Add at least 2 options for dropdown');
    setLoading(true);
    try {
      await axios.post('/api/business/prechat-questions', newQ);
      toast.success('Question added!');
      setNewQ({ question_text: '', question_type: 'text', options: [], is_required: true });
      setOptionInput('');
      setShowAddQ(false);
      loadQuestions();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const updateQuestion = async () => {
    if (!editQ.question_text?.trim()) return toast.error('Please enter a question');
    setLoading(true);
    try {
      await axios.put(`/api/business/prechat-questions/${editQ.id}`, {
        question_text: editQ.question_text,
        question_type: editQ.question_type,
        options: editQ.options || [],
        is_required: editQ.is_required ? 1 : 0,
        is_active: editQ.is_active !== undefined ? editQ.is_active : 1,
        question_order: editQ.question_order || 1
      });
      toast.success('Question updated!');
      setEditQ(null);
      loadQuestions();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed to update'); }
    finally { setLoading(false); }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    await axios.delete(`/api/business/prechat-questions/${id}`);
    toast.success('Deleted');
    loadQuestions();
  };

  const moveQuestion = async (q, dir) => {
    const idx = questions.findIndex(x => x.id === q.id);
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= questions.length) return;
    const swap = questions[swapIdx];
    await axios.put(`/api/business/prechat-questions/${q.id}`, { ...q, options: JSON.stringify(q.options || []), question_order: swap.question_order });
    await axios.put(`/api/business/prechat-questions/${swap.id}`, { ...swap, options: JSON.stringify(swap.options || []), question_order: q.question_order });
    loadQuestions();
  };

  const addOption = (isEdit) => {
    if (!optionInput.trim()) return;
    if (isEdit) setEditQ(prev => ({ ...prev, options: [...(prev.options || []), optionInput.trim()] }));
    else setNewQ(prev => ({ ...prev, options: [...(prev.options || []), optionInput.trim()] }));
    setOptionInput('');
  };

  const removeOption = (idx, isEdit) => {
    if (isEdit) setEditQ(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));
    else setNewQ(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));
  };

  const addCanned = async () => {
    if (!newCanned.title || !newCanned.message) return toast.error('Fill title and message');
    await axios.post('/api/business/canned-replies', newCanned);
    toast.success('Canned reply added!');
    setNewCanned({ title: '', message: '', shortcut: '' });
    loadCanned();
  };



  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Settings</h1>
        <p style={{ color: '#555', fontSize: 13, marginBottom: 24 }}>Manage your business details, questions and quick replies</p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 24, borderBottom: '1px solid #1e1e1e', paddingBottom: 0 }}>
          {[['business', '🏢 Business Details'], ['questions', '❓ Pre-chat Questions'], ['canned', '⚡ Canned Replies'], ['ads', '📣 Ad Integration']].map(([t, l]) => (
            <button key={t} onClick={() => setActiveTab(t)}
              style={{ padding: '10px 20px', background: 'transparent', border: 'none', borderBottom: `2px solid ${activeTab === t ? '#00d55b' : 'transparent'}`, color: activeTab === t ? '#00d55b' : '#555', fontSize: 13, fontWeight: activeTab === t ? 600 : 400, cursor: 'pointer', marginBottom: -1 }}>
              {l}
            </button>
          ))}
        </div>

        {/* BUSINESS DETAILS TAB */}
        {activeTab === 'business' && (
          <div style={{ maxWidth: 560 }}>
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Business Information</h3>
              <p style={{ color: '#555', fontSize: 12, marginBottom: 20 }}>This info appears on your invoices — keep it accurate</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Company Name *</label>
                    <input value={bizForm.company_name} onChange={e => setBizForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Your Company" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Owner Name *</label>
                    <input value={bizForm.owner_name} onChange={e => setBizForm(f => ({ ...f, owner_name: e.target.value }))} placeholder="Your Name" />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Phone *</label>
                  <input value={bizForm.phone} onChange={e => setBizForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91 99999 99999" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Business Address *</label>
                  <input value={bizForm.address} onChange={e => setBizForm(f => ({ ...f, address: e.target.value }))} placeholder="123, Street, City, State - Pincode" />
                  <p style={{ fontSize: 11, color: '#444', marginTop: 4 }}>This will appear on your invoices</p>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>State *</label>
                  <select value={bizForm.state} onChange={e => setBizForm(f => ({ ...f, state: e.target.value }))} style={{ width: '100%' }}>
                    <option value="">Select State</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Bihar">Bihar</option>
                    <option value="Delhi">Delhi</option>
                    <option value="Goa">Goa</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Haryana">Haryana</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Kerala">Kerala</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Punjab">Punjab</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>GSTIN <span style={{ color: '#444' }}>(optional)</span></label>
                  <input value={bizForm.gstin} onChange={e => setBizForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))} placeholder="e.g. 33XXXXXXXXXXXXX" maxLength={15} style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }} />
                  <p style={{ fontSize: 11, color: '#444', marginTop: 4 }}>GST number will appear on invoices</p>
                </div>
                <button onClick={saveBizDetails} disabled={bizLoading}
                  style={{ padding: '11px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none', opacity: bizLoading ? 0.7 : 1 }}>
                  {bizLoading ? 'Saving...' : 'Save Business Details'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PRE-CHAT QUESTIONS TAB */}
        {activeTab === 'questions' && (
          <div>
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Pre-chat Questions</div>
                <div style={{ fontSize: 12, color: '#555', marginTop: 3 }}>
                  {isUnlimited ? 'Unlimited questions (Pack Advanced)' : `${questions.length} / ${planLimit} questions used`}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {!isUnlimited && (
                  <div style={{ background: '#0d0d0d', borderRadius: 20, padding: '4px 14px', fontSize: 12 }}>
                    <span style={{ color: questions.length >= planLimit ? '#ff4444' : '#00d55b', fontWeight: 700 }}>{questions.length}</span>
                    <span style={{ color: '#555' }}> / {planLimit}</span>
                  </div>
                )}
                {(isUnlimited || questions.length < planLimit) && (
                  <button onClick={() => setShowAddQ(true)}
                    style={{ padding: '8px 18px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', border: 'none' }}>
                    + Add Question
                  </button>
                )}
              </div>
            </div>

            {/* Add Modal */}
            {showAddQ && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
                <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Add New Question</h3>
                  <QuestionForm data={newQ} setData={setNewQ} optionInput={optionInput} setOptionInput={setOptionInput} addOption={() => { if(!optionInput.trim()) return; setNewQ(p => ({...p, options: [...(p.options||[]), optionInput.trim()]})); setOptionInput(''); }} removeOption={(i) => setNewQ(p => ({...p, options: p.options.filter((_,x)=>x!==i)}))} />
                  <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <button onClick={() => { setShowAddQ(false); setNewQ({ question_text: '', question_type: 'text', options: [], is_required: true }); }}
                      style={{ flex: 1, padding: '11px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                    <button onClick={addQuestion} disabled={loading}
                      style={{ flex: 2, padding: '11px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                      {loading ? 'Adding...' : 'Add Question'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Modal */}
            {editQ && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
                <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Edit Question</h3>
                  <QuestionForm data={editQ} setData={setEditQ} optionInput={optionInput} setOptionInput={setOptionInput} addOption={() => { if(!optionInput.trim()) return; setEditQ(p => ({...p, options: [...(p.options||[]), optionInput.trim()]})); setOptionInput(''); }} removeOption={(i) => setEditQ(p => ({...p, options: p.options.filter((_,x)=>x!==i)}))} />
                  <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <button onClick={() => setEditQ(null)} style={{ flex: 1, padding: '11px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                    <button onClick={updateQuestion} disabled={loading}
                      style={{ flex: 2, padding: '11px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {questions.length === 0 ? (
              <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>❓</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: '#555', marginBottom: 8 }}>No questions yet</div>
                <button onClick={() => setShowAddQ(true)} style={{ padding: '10px 24px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none' }}>+ Add First Question</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {questions.map((q, idx) => (
                  <div key={q.id} style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,213,91,0.1)', border: '1px solid rgba(0,213,91,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#00d55b', flexShrink: 0 }}>{idx + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 500 }}>{q.question_text}</span>
                        {q.is_required ? <span style={{ fontSize: 10, color: '#ff4444' }}>*required</span> : <span style={{ fontSize: 10, color: '#555' }}>optional</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, padding: '2px 8px', background: '#1a1a1a', borderRadius: 20, color: '#888' }}>
                          {QUESTION_TYPES.find(t => t.value === q.question_type)?.label || q.question_type}
                        </span>
                        {q.options?.length > 0 && <span style={{ fontSize: 11, color: '#555' }}>{q.options.join(' · ')}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      <button onClick={() => moveQuestion(q, 'up')} disabled={idx === 0} style={{ padding: '4px 8px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: idx === 0 ? '#333' : '#888', borderRadius: 6, cursor: idx === 0 ? 'default' : 'pointer', fontSize: 12 }}>↑</button>
                      <button onClick={() => moveQuestion(q, 'down')} disabled={idx === questions.length - 1} style={{ padding: '4px 8px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: idx === questions.length - 1 ? '#333' : '#888', borderRadius: 6, cursor: idx === questions.length - 1 ? 'default' : 'pointer', fontSize: 12 }}>↓</button>
                      <button onClick={() => setEditQ({ ...q, options: q.options || [] })} style={{ padding: '4px 10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✏️ Edit</button>
                      <button onClick={() => deleteQuestion(q.id)} style={{ padding: '4px 10px', background: '#1a0000', border: '1px solid #ff4444', color: '#ff4444', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>🗑 Delete</button>
                    </div>
                  </div>
                ))}
                {!isUnlimited && questions.length >= planLimit && (
                  <div style={{ background: '#0a0a1a', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 13, color: '#a78bfa' }}>Question limit reached ({planLimit}/{planLimit})</div>
                      <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{user?.plan_key === 'pack_one' ? 'Upgrade to Pack Two for 12 questions' : user?.plan_key === 'pack_two' ? 'Upgrade to Pack Three for 20 questions' : 'Upgrade to Pack Advanced for unlimited'}</div>
                    </div>
                    <a href="/dashboard/billing" style={{ padding: '7px 16px', background: 'rgba(124,58,237,0.15)', border: '1px solid #7c3aed', color: '#a78bfa', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>Upgrade →</a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* CANNED REPLIES TAB */}
        {activeTab === 'ads' && (
          <div style={{ maxWidth: 600 }}>
            {!hasAdsIntegration ? (
              <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>📣</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Ad Platform Integration</div>
                <div style={{ fontSize: 13, color: '#555', marginBottom: 20 }}>This feature is available on Pack Two and Pack Advanced plans.</div>
                <div style={{ display: 'inline-block', padding: '10px 24px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 13 }}>Upgrade Plan</div>
              </div>
            ) : (
              <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 24 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>📣 Ad Platform Integration</h3>
                <p style={{ fontSize: 13, color: '#555', marginBottom: 24 }}>Connect your Meta and Google Ads to automatically capture leads into your CRM.</p>

                {/* Meta */}
                <div style={{ marginBottom: 24, padding: 20, background: '#0d0d0d', borderRadius: 10, border: '1px solid #1e1e1e' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(24,119,242,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📘</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Meta (Facebook / Instagram)</div>
                      <div style={{ fontSize: 12, color: '#555' }}>Leads from your Facebook & Instagram ads</div>
                    </div>
                  </div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Your Facebook Page ID</label>
                  <input
                    value={adsForm.meta_page_id}
                    onChange={e => setAdsForm(p => ({ ...p, meta_page_id: e.target.value }))}
                    placeholder="e.g. 123456789012345"
                    style={{ width: '100%', padding: '10px 14px', background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                  />
                  <div style={{ fontSize: 11, color: '#555', marginTop: 6 }}>
                    👉 Go to your Facebook Page → About → scroll down → copy Page ID
                  </div>
                </div>

                {/* Google */}
                <div style={{ marginBottom: 24, padding: 20, background: '#0d0d0d', borderRadius: 10, border: '1px solid #1e1e1e' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(52,168,83,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🟢</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Google Ads</div>
                      <div style={{ fontSize: 12, color: '#555' }}>Leads from your Google Ads lead forms</div>
                    </div>
                  </div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Google Ads Webhook Secret Key</label>
                  <input
                    value={adsForm.google_ads_secret}
                    onChange={e => setAdsForm(p => ({ ...p, google_ads_secret: e.target.value }))}
                    placeholder="Paste the key from Google Ads webhook setup"
                    style={{ width: '100%', padding: '10px 14px', background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                  />
                  <div style={{ fontSize: 11, color: '#555', marginTop: 6 }}>
                    👉 Google Ads → Assets → Lead Form → Lead Delivery → Webhook → copy the Key
                  </div>
                </div>

                {/* Webhook URLs info box */}
                <div style={{ padding: 16, background: 'rgba(0,213,91,0.05)', border: '1px solid rgba(0,213,91,0.2)', borderRadius: 10, marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#00d55b', marginBottom: 10 }}>📋 Your Webhook URLs</div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Meta Webhook URL:</div>
                  <div style={{ fontSize: 11, color: '#00d55b', fontFamily: 'monospace', background: '#0d0d0d', padding: '6px 10px', borderRadius: 6, marginBottom: 10, wordBreak: 'break-all' }}>
                    https://api.mschat.msdiscoveries.com/api/ad-leads/meta
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Google Webhook URL:</div>
                  <div style={{ fontSize: 11, color: '#00d55b', fontFamily: 'monospace', background: '#0d0d0d', padding: '6px 10px', borderRadius: 6, wordBreak: 'break-all' }}>
                    https://api.mschat.msdiscoveries.com/api/ad-leads/google
                  </div>
                </div>

                <button onClick={saveAdsSettings} disabled={adsLoading}
                  style={{ width: '100%', padding: '12px', background: adsLoading ? '#1a1a1a' : '#00d55b', color: adsLoading ? '#555' : '#000', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: adsLoading ? 'not-allowed' : 'pointer' }}>
                  {adsLoading ? 'Saving...' : '💾 Save Ads Integration Settings'}
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'canned' && (
          <div>
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Add Canned Reply</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 10, marginBottom: 12, alignItems: 'end' }}>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Title</label>
                  <input value={newCanned.title} onChange={e => setNewCanned(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Greeting" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Message</label>
                  <input value={newCanned.message} onChange={e => setNewCanned(f => ({ ...f, message: e.target.value }))} placeholder="Full message text" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Shortcut</label>
                  <input value={newCanned.shortcut} onChange={e => setNewCanned(f => ({ ...f, shortcut: e.target.value }))} placeholder="/hi" />
                </div>
              </div>
              <button onClick={addCanned} style={{ padding: '9px 20px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', border: 'none' }}>+ Add Reply</button>
            </div>
            {cannedReplies.length === 0 ? (
              <div style={{ color: '#333', fontSize: 13, textAlign: 'center', padding: 32 }}>No canned replies yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {cannedReplies.map(cr => (
                  <div key={cr.id} style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{cr.title}</span>
                        {cr.shortcut && <span style={{ fontSize: 11, color: '#00d55b', background: 'rgba(0,213,91,0.1)', padding: '1px 8px', borderRadius: 10 }}>/{cr.shortcut}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#555' }}>{cr.message}</div>
                    </div>
                    <button onClick={async () => { await axios.delete(`/api/business/canned-replies/${cr.id}`).catch(() => {}); loadCanned(); toast.success('Deleted'); }}
                      style={{ padding: '4px 10px', background: '#1a0000', border: '1px solid #ff4444', color: '#ff4444', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Delete</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashLayout>
  );
}